import { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Clock, Loader2, RefreshCw, ShieldAlert, XCircle } from "lucide-react";
import { toast } from "sonner";

import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

type TestimonialStatus = "pending" | "approved" | "published" | "rejected" | "archived";

type TestimonialRow = {
  id: string;
  full_name: string;
  email: string;
  role_title: string | null;
  organization: string | null;
  quote: string;
  context: string | null;
  rating: number | null;
  consent_publish_quote: boolean;
  consent_publish_name: boolean;
  consent_publish_org: boolean;
  consent_use_marketing: boolean;
  release_effective_date: string | null;
  release_expires_at: string | null;
  signature_name: string | null;
  signed_at: string | null;
  status: TestimonialStatus;
  moderator_notes: string | null;
  moderated_at: string | null;
  published_at: string | null;
  created_at: string;
};

type EditableFields = Pick<
  TestimonialRow,
  "role_title" | "organization" | "quote" | "context" | "rating" | "release_effective_date" | "release_expires_at"
>;

const STATUS_TONE: Record<TestimonialStatus, string> = {
  pending: "bg-amber-500/15 text-amber-600 border-amber-500/30",
  approved: "bg-sky-500/15 text-sky-600 border-sky-500/30",
  published: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30",
  rejected: "bg-destructive/15 text-destructive border-destructive/30",
  archived: "bg-muted text-muted-foreground border-border",
};

const today = () => new Date().toISOString().slice(0, 10);

const fmt = (value: string | null) =>
  value ? new Date(value).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—";

type ReleaseState = {
  ok: boolean;
  tone: "ok" | "warn" | "blocked";
  label: string;
  detail: string;
};

/** Mirrors the public read policy: quote consent + signature + active release window. */
function evaluateRelease(row: EditableFields & Pick<TestimonialRow, "consent_publish_quote" | "signature_name" | "signed_at">): ReleaseState {
  const now = today();
  if (!row.consent_publish_quote) {
    return { ok: false, tone: "blocked", label: "No quote consent", detail: "Submitter did not consent to publishing the quote. Publishing is blocked." };
  }
  if (!row.signature_name || row.signature_name.trim().length < 2 || !row.signed_at) {
    return { ok: false, tone: "blocked", label: "Unsigned release", detail: "A signature and signing timestamp are required before publishing." };
  }
  if (!row.release_effective_date) {
    return { ok: false, tone: "blocked", label: "No effective date", detail: "Set a release effective date before publishing." };
  }
  if (row.release_expires_at && row.release_expires_at < row.release_effective_date) {
    return { ok: false, tone: "blocked", label: "Inverted window", detail: "The expiry date is before the effective date." };
  }
  if (row.release_effective_date > now) {
    return { ok: false, tone: "warn", label: `Scheduled — starts ${row.release_effective_date}`, detail: "The release window has not opened yet, so the testimonial would stay hidden publicly." };
  }
  if (row.release_expires_at && row.release_expires_at < now) {
    return { ok: false, tone: "warn", label: `Expired ${row.release_expires_at}`, detail: "The release has expired. Extend the expiry date to publish again." };
  }
  return {
    ok: true,
    tone: "ok",
    label: row.release_expires_at ? `Active until ${row.release_expires_at}` : "Active — no expiry",
    detail: "Consent and release window are valid. Safe to publish.",
  };
}

type ChecklistItem = { key: string; label: string; ok: boolean; hint: string; value: string };

/** Required release fields the database trigger enforces before a testimonial can be published. */
function publishChecklist(
  row: EditableFields & Pick<TestimonialRow, "consent_publish_quote" | "signature_name" | "signed_at">,
): ChecklistItem[] {
  const signature = (row.signature_name ?? "").trim();
  const effective = row.release_effective_date;
  const expires = row.release_expires_at;
  return [
    {
      key: "consent_publish_quote",
      label: "Consent to publish quote",
      ok: row.consent_publish_quote === true,
      hint: "The submitter must re-submit the release with quote consent checked. This cannot be overridden.",
      value: row.consent_publish_quote ? "Granted" : "Missing",
    },
    {
      key: "signature_name",
      label: "Signature on release",
      ok: signature.length >= 2,
      hint: "Ask the submitter to sign the release from their testimonial link.",
      value: signature || "Missing",
    },
    {
      key: "signed_at",
      label: "Signed timestamp",
      ok: Boolean(row.signed_at),
      hint: "Recorded automatically when the release is signed.",
      value: row.signed_at ? fmt(row.signed_at) : "Missing",
    },
    {
      key: "release_effective_date",
      label: "Release effective date",
      ok: Boolean(effective) && (!expires || expires >= (effective as string)),
      hint: !effective
        ? "Set an effective date in the field below."
        : "The expiry date must be on or after the effective date.",
      value: effective ? `${effective}${expires ? ` → ${expires}` : " → no expiry"}` : "Missing",
    },
  ];
}

const PublishChecklist = ({ items }: { items: ChecklistItem[] }) => {
  const missing = items.filter((i) => !i.ok);
  return (
    <section
      aria-label="Publish requirements"
      className={`rounded-md border p-3 ${
        missing.length ? "border-destructive/30 bg-destructive/5" : "border-emerald-500/30 bg-emerald-500/5"
      }`}
    >
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide">
        Consent checklist{" "}
        <span className={missing.length ? "text-destructive" : "text-emerald-600"}>
          {items.length - missing.length}/{items.length} complete
        </span>
      </h3>
      <ul className="space-y-1.5">
        {items.map((item) => (
          <li key={item.key} className="flex items-start gap-2 text-xs">
            {item.ok ? (
              <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" aria-hidden="true" />
            ) : (
              <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive" aria-hidden="true" />
            )}
            <div>
              <span className={item.ok ? "font-medium text-foreground" : "font-semibold text-destructive"}>
                {item.label}
              </span>
              <span className="text-muted-foreground"> — {item.value}</span>
              {!item.ok && <p className="text-destructive/90">{item.hint}</p>}
            </div>
          </li>
        ))}
      </ul>
      <p className="mt-2 text-[11px] text-muted-foreground">
        {missing.length
          ? "Publishing stays disabled until every required item above is complete."
          : "All required release fields are complete."}
      </p>
    </section>
  );
};

const ConsentChip = ({ ok, label }: { ok: boolean; label: string }) => (
  <span
    className={`rounded-full border px-2 py-0.5 text-[11px] ${
      ok ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-600" : "border-border bg-muted text-muted-foreground"
    }`}
  >
    {ok ? "✓" : "✕"} {label}
  </span>
);

const ReleaseBanner = ({ state }: { state: ReleaseState }) => {
  const tone =
    state.tone === "ok"
      ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700"
      : state.tone === "warn"
        ? "border-amber-500/30 bg-amber-500/10 text-amber-700"
        : "border-destructive/30 bg-destructive/10 text-destructive";
  const Icon = state.tone === "ok" ? CheckCircle2 : state.tone === "warn" ? Clock : AlertTriangle;
  return (
    <div className={`flex items-start gap-2 rounded-md border p-3 text-xs ${tone}`} role="status">
      <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <div>
        <div className="font-semibold">{state.label}</div>
        <p className="opacity-90">{state.detail}</p>
      </div>
    </div>
  );
};

const TestimonialCard = ({
  row,
  busy,
  onSave,
  onStatus,
}: {
  row: TestimonialRow;
  busy: boolean;
  onSave: (row: TestimonialRow, edits: EditableFields, note: string) => Promise<void>;
  onStatus: (row: TestimonialRow, next: TestimonialStatus, edits: EditableFields, note: string) => Promise<void>;
}) => {
  const [edits, setEdits] = useState<EditableFields>({
    role_title: row.role_title,
    organization: row.organization,
    quote: row.quote,
    context: row.context,
    rating: row.rating,
    release_effective_date: row.release_effective_date,
    release_expires_at: row.release_expires_at,
  });
  const [note, setNote] = useState("");

  const release = useMemo(
    () =>
      evaluateRelease({
        ...edits,
        consent_publish_quote: row.consent_publish_quote,
        signature_name: row.signature_name,
        signed_at: row.signed_at,
      }),
    [edits, row.consent_publish_quote, row.signature_name, row.signed_at],
  );

  const checklist = useMemo(
    () =>
      publishChecklist({
        ...edits,
        consent_publish_quote: row.consent_publish_quote,
        signature_name: row.signature_name,
        signed_at: row.signed_at,
      }),
    [edits, row.consent_publish_quote, row.signature_name, row.signed_at],
  );
  const checklistMissing = checklist.filter((i) => !i.ok);
  const canPublish = release.ok && checklistMissing.length === 0;

  const dirty = (Object.keys(edits) as (keyof EditableFields)[]).some((k) => (edits[k] ?? null) !== (row[k] ?? null));
  const set = <K extends keyof EditableFields>(key: K, value: EditableFields[K]) =>
    setEdits((prev) => ({ ...prev, [key]: value }));

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div>
          <CardTitle className="text-base">
            {row.full_name}
            {row.consent_publish_name ? "" : " (name withheld)"}
          </CardTitle>
          <p className="text-sm text-muted-foreground">{[row.organization, row.email].filter(Boolean).join(" · ")}</p>
        </div>
        <Badge variant="outline" className={STATUS_TONE[row.status]}>
          {row.status}
        </Badge>
      </CardHeader>

      <CardContent className="space-y-4">
        <ReleaseBanner state={release} />

        <PublishChecklist items={checklist} />


        <div className="grid gap-2 text-xs text-muted-foreground sm:grid-cols-2">
          <div><span className="font-medium text-foreground">Submitted: </span>{fmt(row.created_at)}</div>
          <div><span className="font-medium text-foreground">Signed: </span>{fmt(row.signed_at)} {row.signature_name ? `by ${row.signature_name}` : ""}</div>
          <div><span className="font-medium text-foreground">Last moderated: </span>{fmt(row.moderated_at)}</div>
          <div><span className="font-medium text-foreground">Published: </span>{fmt(row.published_at)}</div>
        </div>

        <div className="flex flex-wrap gap-1.5">
          <ConsentChip ok={row.consent_publish_quote} label="quote" />
          <ConsentChip ok={row.consent_publish_name} label="name" />
          <ConsentChip ok={row.consent_publish_org} label="organization" />
          <ConsentChip ok={row.consent_use_marketing} label="marketing" />
        </div>

        <div className="space-y-2">
          <Label htmlFor={`quote-${row.id}`}>Quote (published copy)</Label>
          <Textarea
            id={`quote-${row.id}`}
            rows={4}
            value={edits.quote}
            onChange={(e) => set("quote", e.target.value)}
          />
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor={`role-${row.id}`}>Role / title</Label>
            <Input id={`role-${row.id}`} value={edits.role_title ?? ""} onChange={(e) => set("role_title", e.target.value || null)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor={`org-${row.id}`}>
              Organization {row.consent_publish_org ? "" : "(no consent — leave blank)"}
            </Label>
            <Input
              id={`org-${row.id}`}
              value={edits.organization ?? ""}
              disabled={!row.consent_publish_org}
              onChange={(e) => set("organization", e.target.value || null)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={`rating-${row.id}`}>Rating (1–5)</Label>
            <Input
              id={`rating-${row.id}`}
              type="number"
              min={1}
              max={5}
              value={edits.rating ?? ""}
              onChange={(e) => set("rating", e.target.value === "" ? null : Number(e.target.value))}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={`context-${row.id}`}>Context (internal / caption)</Label>
            <Input id={`context-${row.id}`} value={edits.context ?? ""} onChange={(e) => set("context", e.target.value || null)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor={`eff-${row.id}`}>Release effective date</Label>
            <Input
              id={`eff-${row.id}`}
              type="date"
              value={edits.release_effective_date ?? ""}
              onChange={(e) => set("release_effective_date", e.target.value || null)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={`exp-${row.id}`}>Release expires (blank = never)</Label>
            <Input
              id={`exp-${row.id}`}
              type="date"
              value={edits.release_expires_at ?? ""}
              onChange={(e) => set("release_expires_at", e.target.value || null)}
            />
          </div>
        </div>

        {row.moderator_notes ? (
          <p className="text-xs text-muted-foreground">
            <span className="font-medium text-foreground">Previous notes: </span>
            {row.moderator_notes}
          </p>
        ) : null}

        <div className="space-y-2">
          <Label htmlFor={`note-${row.id}`} className="text-xs">Moderator notes (saved with the decision)</Label>
          <Textarea
            id={`note-${row.id}`}
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Why are you approving, editing, publishing, or rejecting this?"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="secondary" disabled={busy || !dirty} onClick={() => void onSave(row, edits, note)}>
            Save edits
          </Button>
          {row.status !== "approved" && row.status !== "published" && (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => void onStatus(row, "approved", edits, note)}>
              Approve
            </Button>
          )}
          {row.status !== "published" && (
            <Button
              size="sm"
              disabled={busy || !canPublish}
              title={
                checklistMissing.length
                  ? `Missing: ${checklistMissing.map((i) => i.label).join(", ")}`
                  : release.ok
                    ? undefined
                    : release.detail
              }
              onClick={() => void onStatus(row, "published", edits, note)}
            >
              Publish
            </Button>
          )}
          {row.status !== "rejected" && (
            <Button size="sm" variant="destructive" disabled={busy} onClick={() => void onStatus(row, "rejected", edits, note)}>
              Reject
            </Button>
          )}
          {row.status !== "archived" && (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => void onStatus(row, "archived", edits, note)}>
              Archive
            </Button>
          )}
          {busy && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        </div>
      </CardContent>
    </Card>
  );
};

const FILTERS = ["queue", "published", "all"] as const;
type Filter = (typeof FILTERS)[number];

const AdminTestimonials = () => {
  const { user, loading: authLoading } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [rows, setRows] = useState<TestimonialRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>("queue");

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setIsAdmin(false);
      return;
    }
    supabase
      .rpc("has_role", { _user_id: user.id, _role: "admin" })
      .then(({ data, error }) => setIsAdmin(!error && data === true));
  }, [authLoading, user]);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("testimonial_submissions")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) toast.error(error.message);
    setRows((data as TestimonialRow[]) ?? []);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (isAdmin) void load();
  }, [isAdmin, load]);

  const visible = useMemo(() => {
    if (filter === "all") return rows;
    if (filter === "published") return rows.filter((r) => r.status === "published");
    return rows.filter((r) => r.status === "pending" || r.status === "approved");
  }, [rows, filter]);

  const pendingCount = rows.filter((r) => r.status === "pending").length;
  const expiringCount = rows.filter(
    (r) => r.status === "published" && r.release_expires_at && r.release_expires_at < today(),
  ).length;

  const persistEdits = async (row: TestimonialRow, edits: EditableFields) => {
    const { error } = await supabase
      .from("testimonial_submissions")
      .update({
        role_title: edits.role_title,
        organization: row.consent_publish_org ? edits.organization : null,
        quote: edits.quote,
        context: edits.context,
        rating: edits.rating,
        release_effective_date: edits.release_effective_date,
        release_expires_at: edits.release_expires_at,
      })
      .eq("id", row.id);
    return error;
  };

  const onSave = async (row: TestimonialRow, edits: EditableFields, note: string) => {
    setBusyId(row.id);
    const error = await persistEdits(row, edits);
    if (!error && note.trim()) {
      await supabase.rpc("set_testimonial_release_status", {
        _id: row.id,
        _status: row.status,
        _notes: note.trim(),
      });
    }
    setBusyId(null);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Edits saved");
    void load();
  };

  const onStatus = async (row: TestimonialRow, next: TestimonialStatus, edits: EditableFields, note: string) => {
    setBusyId(row.id);
    const editError = await persistEdits(row, edits);
    if (editError) {
      setBusyId(null);
      toast.error(editError.message);
      return;
    }
    const { error } = await supabase.rpc("set_testimonial_release_status", {
      _id: row.id,
      _status: next,
      _notes: note.trim() ? note.trim() : null,
    });
    setBusyId(null);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(`Testimonial ${next}`);
    void load();
  };

  if (authLoading || isAdmin === null) {
    return (
      <main className="flex min-h-dvh items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </main>
    );
  }

  if (!isAdmin) {
    return (
      <main className="mx-auto flex min-h-dvh max-w-md flex-col items-center justify-center gap-4 px-6 text-center">
        <ShieldAlert className="h-8 w-8 text-muted-foreground" aria-hidden="true" />
        <h1 className="text-xl font-semibold">Admin access required</h1>
        <p className="text-sm text-muted-foreground">
          Sign in with an administrator account to moderate testimonials.
        </p>
        <Button asChild>
          <Link to="/auth">Sign in</Link>
        </Button>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-4xl px-4 py-10">
      <Helmet>
        <title>Testimonial Moderation | Admin</title>
        <meta name="description" content="Internal admin view to review, edit, and publish testimonial releases." />
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Testimonial moderation</h1>
          <p className="text-sm text-muted-foreground">
            {pendingCount} pending {pendingCount === 1 ? "release" : "releases"}
            {expiringCount > 0 ? ` · ${expiringCount} published with an expired release window` : ""}.
          </p>
        </div>
        <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" asChild>
          <Link to="/admin/testimonials/preview">Visitor preview</Link>
        </Button>
        <Button variant="outline" size="sm" asChild>
          <Link to="/admin/testimonials/audit">Audit log</Link>
        </Button>
        <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
          {loading ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
          )}
          Refresh
        </Button>
        </div>
      </header>

      <Tabs value={filter} onValueChange={(v) => setFilter(v as Filter)}>
        <TabsList>
          <TabsTrigger value="queue">Queue</TabsTrigger>
          <TabsTrigger value="published">Published</TabsTrigger>
          <TabsTrigger value="all">All</TabsTrigger>
        </TabsList>
        <TabsContent value={filter} className="mt-4 space-y-4">
          {visible.length === 0 && <p className="text-sm text-muted-foreground">Nothing to review here.</p>}
          {visible.map((row) => (
            <TestimonialCard
              key={`${row.id}-${row.moderated_at ?? ""}-${row.quote.length}`}
              row={row}
              busy={busyId === row.id}
              onSave={onSave}
              onStatus={onStatus}
            />
          ))}
        </TabsContent>
      </Tabs>
    </main>
  );
};

export default AdminTestimonials;
